import { request } from '@mairistem/tools';
import dayjs from 'dayjs';
import React from 'react';
import { ModalConfirm } from '@/components/Autre';
import { ModeReglementContextProvider, RecuContextProvider } from '@/components/Context';
import ReglementPointageContextProvider from '@/components/Context/ReglementPointageContextProvider';
import { MODES_REGLEMENTS } from '@/constants';
import { ArretePeriodiquePointageContext } from '@/contexts';
import { useArretePointageSelection } from '@/hooks';
import { getArretePrevisionnelPeriodique } from '@/routes';
import type { ArretePeriodeModeType } from '@/types';
import { useRegieStore } from '@/zustand';

type PointageEtEditionsWrapperProps = {
  children: React.ReactNode;
};

type PeriodeChange = { periodeMode?: ArretePeriodeModeType; dateFin?: dayjs.Dayjs };

/**
 * Enveloppe les étapes 2 (pointage) et 3 (éditions) de la feature arrêtés périodiques avec :
 * - les 12 contextes de règlements par mode de règlement (partagés entre les deux étapes
 *   pour éviter un rechargement lors de la transition),
 * - le contexte de sélection API (ArretePeriodiquePointageContext) qui persiste les
 *   cochages/décochages sur le prévisionnel.
 *
 * Il porte également la sélection de période de pointage (mode + date de fin), commune à tous
 * les modes de règlement : l'état vit ici en simple useState et se propage aux 12 providers
 * (via props, pour le filtrage côté client) et aux écrans pointage/éditions (via le contexte).
 *
 * Skill async-parallel : chaque ReglementPointageContextProvider déclenche son propre
 * fetch en parallèle dès le montage du wrapper.
 */
const PointageEtEditionsWrapper = (props: PointageEtEditionsWrapperProps) => {
  const { children } = props;
  const regie = useRegieStore((state) => state.regie);

  const selection = useArretePointageSelection(!!regie?.identifiant);

  // État de la sélection de période (valeurs COMMITTÉES : les contrôles UI s'y bornent, ce
  // qui assure un revert automatique si l'utilisateur annule la modale de confirmation).
  const [periodeMode, setPeriodeMode] = React.useState<ArretePeriodeModeType>('dernierArrete');
  const [dateFin, setDateFin] = React.useState<dayjs.Dayjs>(() => dayjs());
  const [dateDebut, setDateDebut] = React.useState<dayjs.Dayjs | null>(null);
  // Changement en attente de confirmation (ouvre la modale). Jamais committé tant que
  // l'utilisateur n'a pas confirmé l'effacement des pointages.
  const [pending, setPending] = React.useState<PeriodeChange | null>(null);
  const [clearing, setClearing] = React.useState<boolean>(false);

  // Au montage : prévisionnel STANDARD (sans dateFin) pour initialiser la date de début
  // (lecture seule) et la date de fin par défaut. Échec/422 NON bloquant : on retombe sur
  // dateDebut = null et dateFin = aujourd'hui.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response: any = await request.get(getArretePrevisionnelPeriodique());
        if (cancelled) return;
        const candidate = response && 'data' in response ? response.data : response;
        if (candidate && typeof candidate === 'object' && 'dateDebutPeriode' in candidate) {
          setDateDebut(dayjs(candidate.dateDebutPeriode));
          setDateFin(candidate.dateFinPeriode ? dayjs(candidate.dateFinPeriode) : dayjs());
        } else {
          setDateDebut(null);
          setDateFin(dayjs());
        }
      } catch {
        if (!cancelled) {
          setDateDebut(null);
          setDateFin(dayjs());
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const commitPeriode = React.useCallback((next: PeriodeChange) => {
    if (next.periodeMode !== undefined) setPeriodeMode(next.periodeMode);
    if (next.dateFin !== undefined) setDateFin(next.dateFin);
  }, []);

  const requestPeriodeChange = React.useCallback(
    (next: PeriodeChange) => {
      // Ignorer les no-op (valeur identique au committé).
      const sameMode = next.periodeMode === undefined || next.periodeMode === periodeMode;
      const sameDate = next.dateFin === undefined || next.dateFin.isSame(dateFin, 'day');
      if (sameMode && sameDate) return;

      // Aucun pointage → application immédiate sans modale.
      if (selection.selected.size === 0) {
        commitPeriode(next);
        return;
      }
      // Sinon, on attend la confirmation d'effacement des pointages.
      setPending(next);
    },
    [periodeMode, dateFin, selection.selected, commitPeriode],
  );

  const handleConfirmPeriode = React.useCallback(async () => {
    if (!pending) return;
    setClearing(true);
    try {
      // Effacer TOUS les pointages AVANT de committer (applyBulk fait un optimistic update
      // synchrone qui, sinon, se recalculerait sur la nouvelle liste filtrée).
      await selection.applyBulk([]);
      commitPeriode(pending);
    } finally {
      setClearing(false);
      setPending(null);
    }
  }, [pending, selection, commitPeriode]);

  const contextValue = React.useMemo(
    () => ({
      selected: selection.selected,
      loading: selection.loading,
      saving: selection.saving,
      toggle: selection.toggle,
      applyBulk: selection.applyBulk,
      periodeMode,
      dateDebut,
      dateFin,
      requestPeriodeChange,
    }),
    [
      selection.selected,
      selection.loading,
      selection.saving,
      selection.toggle,
      selection.applyBulk,
      periodeMode,
      dateDebut,
      dateFin,
      requestPeriodeChange,
    ],
  );

  return (
    <>
      <ModeReglementContextProvider autoload="régie">
        <RecuContextProvider standAlone>
          <ReglementPointageContextProvider
            forArrete
            code={MODES_REGLEMENTS.CHEQUE}
            periodeMode={periodeMode}
            dateFin={dateFin}
          >
            <ReglementPointageContextProvider
              forArrete
              code={MODES_REGLEMENTS.NUMERAIRE}
              periodeMode={periodeMode}
              dateFin={dateFin}
            >
              <ReglementPointageContextProvider
                forArrete
                code={MODES_REGLEMENTS.CESU}
                periodeMode={periodeMode}
                dateFin={dateFin}
              >
                <ReglementPointageContextProvider
                  forArrete
                  code={MODES_REGLEMENTS.CHEQUE_VAC}
                  periodeMode={periodeMode}
                  dateFin={dateFin}
                >
                  <ReglementPointageContextProvider
                    forArrete
                    code={MODES_REGLEMENTS.CARTE_BANCAIRE}
                    periodeMode={periodeMode}
                    dateFin={dateFin}
                  >
                    <ReglementPointageContextProvider
                      forArrete
                      code={MODES_REGLEMENTS.VIREMENT}
                      periodeMode={periodeMode}
                      dateFin={dateFin}
                    >
                      <ReglementPointageContextProvider
                        forArrete
                        code={MODES_REGLEMENTS.PRELEVEMENT}
                        periodeMode={periodeMode}
                        dateFin={dateFin}
                      >
                        <ReglementPointageContextProvider
                          forArrete
                          code={MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET}
                          periodeMode={periodeMode}
                          dateFin={dateFin}
                        >
                          <ReglementPointageContextProvider
                            forArrete
                            code={MODES_REGLEMENTS.PRELEVEMENT_INTERNET}
                            periodeMode={periodeMode}
                            dateFin={dateFin}
                          >
                            <ReglementPointageContextProvider
                              forArrete
                              code={MODES_REGLEMENTS.TICKET}
                              periodeMode={periodeMode}
                              dateFin={dateFin}
                            >
                              <ReglementPointageContextProvider
                                forArrete
                                code={MODES_REGLEMENTS.BON_CCAS}
                                periodeMode={periodeMode}
                                dateFin={dateFin}
                              >
                                <ReglementPointageContextProvider
                                  forArrete
                                  code={MODES_REGLEMENTS.BON_CAF}
                                  periodeMode={periodeMode}
                                  dateFin={dateFin}
                                >
                                  <ArretePeriodiquePointageContext.Provider value={contextValue}>
                                    {children}
                                  </ArretePeriodiquePointageContext.Provider>
                                </ReglementPointageContextProvider>
                              </ReglementPointageContextProvider>
                            </ReglementPointageContextProvider>
                          </ReglementPointageContextProvider>
                        </ReglementPointageContextProvider>
                      </ReglementPointageContextProvider>
                    </ReglementPointageContextProvider>
                  </ReglementPointageContextProvider>
                </ReglementPointageContextProvider>
              </ReglementPointageContextProvider>
            </ReglementPointageContextProvider>
          </ReglementPointageContextProvider>
        </RecuContextProvider>
      </ModeReglementContextProvider>

      {pending && (
        <ModalConfirm
          header="Modification de la période de pointage"
          content="Tous les pointages effectués seront effacés. Voulez-vous continuer ?"
          confirmText="Continuer"
          cancelText="Annuler"
          loading={clearing}
          onConfirm={handleConfirmPeriode}
          onCancel={() => setPending(null)}
        />
      )}
    </>
  );
};

export default PointageEtEditionsWrapper;
